import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import './Envase.css';

const TIPOS_DOC = [
    { codigo: 'FCO', nombre: 'Factura Contado' },
    { codigo: 'FCR', nombre: 'Factura Credito' },
    { codigo: 'FEX', nombre: 'Factura Extranjera' },
    { codigo: 'AUT', nombre: 'Autofactura' },
    { codigo: 'COM', nombre: 'Comprobante' },
    { codigo: 'ND',  nombre: 'Nota de Debito' },
    { codigo: 'NC',  nombre: 'Nota de Credito' },
    { codigo: 'VAR', nombre: 'Varios' },
    { codigo: 'DSP', nombre: 'Despacho' },
    { codigo: 'LIQ', nombre: 'Liquidacion' }
];

const formatearFecha = (f) => {
    if (!f) return '';
    const [y, m, d] = String(f).split('-');
    return `${d}/${m}/${y}`;
};

const obtenerFechaHoy = () => new Date().toISOString().split('T')[0];

const calcularVencimiento = (fechaStr, dias) => {
    if (!fechaStr || dias == null) return '';
    const fecha = new Date(fechaStr + 'T00:00:00');
    fecha.setDate(fecha.getDate() + Number(dias));
    return fecha.toISOString().split('T')[0];
};

const formatearNumero = (v, dec = 2) => {
    if (v == null || v === '') return '0,00';
    return Number(v).toLocaleString('es-PY', {
        minimumFractionDigits: dec, maximumFractionDigits: dec
    });
};

const formularioInicial = () => ({
    idProveedor: '',
    nomProveedor: '',
    rucProveedor: '',
    nfactura: '',
    tipoDoc: 'FCR',
    timbrado: '',
    fecha: obtenerFechaHoy(),
    vence: '',
    idFormapago: '',
    idMoneda: 'PYG',
    cambio: 1,
    monExenta: 0,
    monGravada: 0,
    monIva: 0,
    monTotal: 0,
    obs: '',
    sucursal: 1,
    boca: '',
    nrofacex: '',
    fservicio: 0,
    precioiva: 0,
    detalles: []
});

function SFacompra({ usuario }) {
    const [compras, setCompras] = useState([]);
    const [busqueda, setBusqueda] = useState('');
    const [fechaDesde, setFechaDesde] = useState('');
    const [fechaHasta, setFechaHasta] = useState('');
    const [pagina, setPagina] = useState(1);
    const [porPagina, setPorPagina] = useState(10);

    const [modalAbierto, setModalAbierto] = useState(false);
    const [modalVerAbierto, setModalVerAbierto] = useState(false);
    const [compraVer, setCompraVer] = useState(null);
    const [formulario, setFormulario] = useState(formularioInicial());

    // Buscador de proveedor
    const [busquedaProv, setBusquedaProv] = useState('');
    const [provsEncontrados, setProvsEncontrados] = useState([]);

    // Buscador de productos
    const [busquedaProducto, setBusquedaProducto] = useState('');
    const [productosEncontrados, setProductosEncontrados] = useState([]);
    const [detalleTemp, setDetalleTemp] = useState({
        idProducto: '', descripcion: '', cantidad: 1, precio: 0, gravada: 10, idDeposito: 1
    });

    // Catalogo de formas de pago (para el select del alta y calculo de vencimiento)
    const [formasPago, setFormasPago] = useState([]);

    // Ordenes de compra activas del proveedor (para el boton "Obtener orden")
    const [ordenesActivas, setOrdenesActivas] = useState([]);
    const [modalOrdenAbierto, setModalOrdenAbierto] = useState(false);
    // IDs de las ordenes desde las que se cargaron lineas (para cerrarlas al guardar)
    const [ordenesUsadas, setOrdenesUsadas] = useState([]);

    // Recepciones activas del proveedor (Opcion B: stock ya movido por recepcion)
    const [recepcionesActivas, setRecepcionesActivas] = useState([]);
    const [modalRecepAbierto, setModalRecepAbierto] = useState(false);
    // Recepciones seleccionadas para vincular: [{idSucursal, boca, nrecep}]
    const [recepcionesVinculadas, setRecepcionesVinculadas] = useState([]);
    // Agregado por producto de las recepciones seleccionadas
    // [{ id_producto, cantidad_recibida }]
    const [aggRecep, setAggRecep] = useState([]);

    useEffect(() => { cargar(); cargarCatalogos(); }, []);

    // Cuando cambian las recepciones vinculadas -> pedir agregado por producto
    useEffect(() => {
        if (recepcionesVinculadas.length === 0) { setAggRecep([]); return; }
        const payload = recepcionesVinculadas.map(r => ({
            idSucursal: r.id_sucursal, boca: r.boca, nrecep: r.nrecep
        }));
        axios.post('/api/recepmateriales/agregado-por-producto', payload)
            .then(({ data }) => setAggRecep(data))
            .catch(e => { console.error(e); setAggRecep([]); });
    }, [recepcionesVinculadas]);

    // Volcar las lineas del agregado al detalle de la factura. Se llama al
    // confirmar la seleccion en el modal de recepciones.
    // - Fusiona con detalles existentes (si el producto ya esta, SUMA cantidad)
    // - Marca cada linea con `recepOrigen` (ids "S-B-N") para trazabilidad UI
    const volcarRecepcionesADetalle = () => {
        if (aggRecep.length === 0) {
            setModalRecepAbierto(false);
            return;
        }
        setFormulario(f => {
            const detallesMap = new Map(f.detalles.map(d => [d.idProducto, { ...d }]));
            for (const a of aggRecep) {
                const idProducto = String(a.id_producto).trim();
                const cantidad = Number(a.cantidad_recibida) || 0;
                const gravada = Number(a.gravada) != null ? Number(a.gravada) : 10;
                const precio = Number(a.costoulcom || a.costoprom || 0);
                const idDeposito = a.id_deposito || 1;
                const origen = Array.isArray(a.origen) ? a.origen : [];

                const existente = detallesMap.get(idProducto);
                if (existente) {
                    // Sumar cantidad al detalle existente y recalcular
                    const nuevaCant = Number(existente.cantidad) + cantidad;
                    const calc = calcularLinea(nuevaCant, Number(existente.precio), Number(existente.gravada));
                    detallesMap.set(idProducto, {
                        ...existente,
                        cantidad: nuevaCant,
                        monExenta: calc.exenta,
                        monGravada: calc.gravada,
                        monIva: calc.iva,
                        monTotal: calc.total,
                        recepOrigen: [...(existente.recepOrigen || []), ...origen]
                    });
                } else {
                    const calc = calcularLinea(cantidad, precio, gravada);
                    detallesMap.set(idProducto, {
                        idProducto,
                        nomProducto: (a.descripcion || '').substring(0, 40),
                        idDeposito,
                        cantidad,
                        precio,
                        gravada,
                        monExenta: calc.exenta,
                        monGravada: calc.gravada,
                        monIva: calc.iva,
                        monTotal: calc.total,
                        recepOrigen: origen
                    });
                }
            }
            const detalles = Array.from(detallesMap.values());
            const totales = detalles.reduce((acc, d) => ({
                exenta: acc.exenta + (Number(d.monExenta) || 0),
                gravada: acc.gravada + (Number(d.monGravada) || 0),
                iva: acc.iva + (Number(d.monIva) || 0),
                total: acc.total + (Number(d.monTotal) || 0)
            }), { exenta: 0, gravada: 0, iva: 0, total: 0 });

            return {
                ...f, detalles,
                monExenta: totales.exenta,
                monGravada: totales.gravada,
                monIva: totales.iva,
                monTotal: totales.total
            };
        });
        setModalRecepAbierto(false);
    };

    const cargar = async () => {
        try {
            const r = await axios.get('/api/facompra');
            setCompras(r.data);
        } catch (e) {
            console.error('Error al cargar compras:', e);
            alert('Error al cargar facturas de compra');
        }
    };

    const cargarCatalogos = async () => {
        try {
            const r = await axios.get('/api/fpago');
            setFormasPago(r.data);
        } catch (e) {
            console.error('Error al cargar formas de pago:', e);
        }
    };

    // Handler que actualiza forma de pago y recalcula vence
    const seleccionarFormaPago = (idFpago) => {
        const fp = formasPago.find(f => f.idFpago === idFpago);
        setFormulario(f => ({
            ...f,
            idFormapago: idFpago,
            vence: fp && fp.vdias1 != null ? calcularVencimiento(f.fecha, fp.vdias1) : f.vence
        }));
    };

    // Cuando cambia la fecha, recalcular vence si hay forma de pago seleccionada
    const cambiarFecha = (nuevaFecha) => {
        setFormulario(f => {
            const fp = formasPago.find(x => x.idFpago === f.idFormapago);
            return {
                ...f,
                fecha: nuevaFecha,
                vence: fp && fp.vdias1 != null ? calcularVencimiento(nuevaFecha, fp.vdias1) : f.vence
            };
        });
    };

    // Filtrado
    const filtradas = compras.filter(c => {
        if (fechaDesde && (!c.fecha || c.fecha < fechaDesde)) return false;
        if (fechaHasta && (!c.fecha || c.fecha > fechaHasta)) return false;
        if (busqueda) {
            const q = busqueda.toLowerCase();
            const matches =
                c.nfactura?.toString().includes(busqueda) ||
                (c.nomProveedor || '').toLowerCase().includes(q) ||
                (c.obs || '').toLowerCase().includes(q) ||
                (c.tipoDoc || '').toLowerCase().includes(q);
            if (!matches) return false;
        }
        return true;
    });

    const totalPaginas = Math.max(1, Math.ceil(filtradas.length / porPagina));
    const inicio = (pagina - 1) * porPagina;
    const paginadas = filtradas.slice(inicio, inicio + porPagina);

    // ===== Alta =====
    const abrirModalNuevo = () => {
        setFormulario(formularioInicial());
        setBusquedaProv('');
        setProvsEncontrados([]);
        setBusquedaProducto('');
        setProductosEncontrados([]);
        setDetalleTemp({ idProducto: '', descripcion: '', cantidad: 1, precio: 0, gravada: 10, idDeposito: 1 });
        setOrdenesActivas([]);
        setOrdenesUsadas([]);
        setRecepcionesActivas([]);
        setRecepcionesVinculadas([]);
        setAggRecep([]);
        setModalAbierto(true);
    };

    const cerrarModal = () => setModalAbierto(false);

    // Buscar proveedor
    const buscarProvAPI = async (texto) => {
        setBusquedaProv(texto);
        if (texto.length < 2) { setProvsEncontrados([]); return; }
        try {
            const r = await axios.get('/api/facompra/buscar-proveedor', { params: { busqueda: texto } });
            setProvsEncontrados(r.data);
        } catch (e) { console.error(e); }
    };

    const seleccionarProveedor = async (p) => {
        setFormulario(f => ({
            ...f,
            idProveedor: p.id_proveedor,
            nomProveedor: p.nom_proveedor,
            rucProveedor: p.ruc_prov
        }));
        setBusquedaProv('');
        setProvsEncontrados([]);
        // Cargar ordenes de compra activas del proveedor
        try {
            const r = await axios.get('/api/ordencompra/activas', {
                params: { idProveedor: p.id_proveedor }
            });
            setOrdenesActivas(r.data);
        } catch (e) {
            console.error('Error al cargar ordenes activas:', e);
            setOrdenesActivas([]);
        }
        // Cargar recepciones de materiales ACTIVAS del proveedor
        // (stock ya movido, esperando ser facturado)
        try {
            const r = await axios.get('/api/recepmateriales/activas', {
                params: { idProveedor: p.id_proveedor }
            });
            setRecepcionesActivas(r.data);
        } catch (e) {
            console.error('Error al cargar recepciones activas:', e);
            setRecepcionesActivas([]);
        }
        // Reset seleccion previa
        setRecepcionesVinculadas([]);
    };

    // Cargar detalle de una orden y volcarlo al detalle de la factura
    const seleccionarOrden = async (orden) => {
        try {
            const r = await axios.get(`/api/ordencompra/${orden.ordencompra}/detalle-facturable`);
            const lineas = r.data;
            if (!lineas || lineas.length === 0) {
                alert('La orden no tiene lineas pendientes de facturar');
                return;
            }

            // Convertir cada linea de la orden en un detalle de factura
            // Usa cantidad_pendiente (cantidad - cantidad_facturada) para permitir facturas parciales
            const nuevosDetalles = lineas.map(l => {
                const gravada = Number(l.gravada) || 10;
                const cantidad = Number(l.cantidad_pendiente || l.cantidad);
                const precio = Number(l.precio);
                const calc = calcularLinea(cantidad, precio, gravada);
                return {
                    idProducto: l.id_producto,
                    nomProducto: (l.descripcion || '').substring(0, 40),
                    idDeposito: 1,
                    cantidad,
                    precio,
                    gravada,
                    monExenta: calc.exenta,
                    monGravada: calc.gravada,
                    monIva: calc.iva,
                    monTotal: calc.total,
                    // Origen para el bridge: cual orden aporto esta linea
                    ordencompraOrigen: orden.ordencompra
                };
            });

            // Copiar tambien la forma de pago desde la orden (y recalcular vencimiento con vdias1)
            const idFpagoOrden = orden.id_fpago || null;
            const fp = idFpagoOrden ? formasPago.find(x => x.idFpago === idFpagoOrden) : null;

            // Fusionar con los detalles actuales (evitar duplicados por id_producto)
            setFormulario(f => {
                const existingIds = new Set(f.detalles.map(d => d.idProducto));
                const filtrados = nuevosDetalles.filter(d => !existingIds.has(d.idProducto));
                const detalles = [...f.detalles, ...filtrados];

                const totales = detalles.reduce((acc, d) => ({
                    exenta: acc.exenta + (Number(d.monExenta) || 0),
                    gravada: acc.gravada + (Number(d.monGravada) || 0),
                    iva: acc.iva + (Number(d.monIva) || 0),
                    total: acc.total + (Number(d.monTotal) || 0)
                }), { exenta: 0, gravada: 0, iva: 0, total: 0 });

                // Solo sobreescribir idFormapago/vence si vienen en la orden y el usuario
                // no habia elegido ya una forma de pago manualmente
                const nuevoFpago = (idFpagoOrden && !f.idFormapago) ? idFpagoOrden : f.idFormapago;
                const nuevoVence = (fp && fp.vdias1 != null && (!f.vence || !f.idFormapago))
                    ? calcularVencimiento(f.fecha, fp.vdias1)
                    : f.vence;

                return {
                    ...f, detalles,
                    idFormapago: nuevoFpago,
                    vence: nuevoVence,
                    monExenta: totales.exenta,
                    monGravada: totales.gravada,
                    monIva: totales.iva,
                    monTotal: totales.total
                };
            });

            // Registrar la orden como "usada" (se cerrara al guardar la factura)
            setOrdenesUsadas(prev => prev.includes(orden.ordencompra) ? prev : [...prev, orden.ordencompra]);
            setModalOrdenAbierto(false);
            alert(`Se cargaron ${nuevosDetalles.length} lineas de la orden #${orden.ordencompra}`);
        } catch (e) {
            console.error(e);
            alert('Error al cargar el detalle de la orden');
        }
    };

    // Buscar producto
    // Busqueda de productos: 250 ms tras la ultima tecla; solo se muestra la respuesta de la ultima busqueda
    const busquedaProductoRef = useRef({ timer: null, secuencia: 0 });
    const buscarProductosAPI = (texto) => {
        setBusquedaProducto(texto);
        const ref = busquedaProductoRef.current;
        clearTimeout(ref.timer);
        if (texto.trim().length < 2) { ref.secuencia++; setProductosEncontrados([]); return; }
        const seq = ++ref.secuencia;
        ref.timer = setTimeout(async () => {
            try {
                const r = await axios.get('/api/facompra/buscar-producto', { params: { busqueda: texto } });
                if (seq === ref.secuencia) setProductosEncontrados(r.data);
            } catch (e) { console.error(e); }
        }, 250);
    };

    const seleccionarProducto = (p) => {
        setDetalleTemp({
            idProducto: p.id_producto,
            descripcion: p.descripcion,
            cantidad: 1,
            precio: Number(p.costoprom) || 0,
            gravada: Number(p.gravada) || 10,
            idDeposito: 1
        });
        // Mostrar codigo + descripcion en el input para que el usuario vea ambos
        setBusquedaProducto(`${p.id_producto} - ${p.descripcion}`);
        setProductosEncontrados([]);
    };

    // Calcula la distribucion de IVA de una linea (segun % gravada)
    const calcularLinea = (cantidad, precio, gravada) => {
        const monto = Number(cantidad) * Number(precio);
        let exenta = 0, gravadaSinIva = 0, iva = 0;
        if (gravada === 0 || gravada === '0') {
            exenta = monto;
        } else if (gravada === 5 || gravada === '5') {
            // monto ya incluye IVA
            gravadaSinIva = Math.round(monto / 1.05);
            iva = monto - gravadaSinIva;
        } else if (gravada === 10 || gravada === '10') {
            gravadaSinIva = Math.round(monto / 1.10);
            iva = monto - gravadaSinIva;
        }
        return { exenta, gravada: gravadaSinIva, iva, total: monto };
    };

    const agregarDetalle = () => {
        if (!detalleTemp.idProducto) { alert('Seleccione un producto'); return; }
        if (detalleTemp.cantidad <= 0) { alert('Cantidad debe ser mayor a 0'); return; }
        if (detalleTemp.precio < 0) { alert('El precio no puede ser negativo'); return; }
        if (formulario.detalles.some(d => d.idProducto === detalleTemp.idProducto)) {
            alert('El producto ya esta en el detalle'); return;
        }

        const calc = calcularLinea(detalleTemp.cantidad, detalleTemp.precio, detalleTemp.gravada);
        setFormulario(f => {
            const nuevoDetalle = {
                idProducto: detalleTemp.idProducto,
                nomProducto: detalleTemp.descripcion?.substring(0, 40),
                idDeposito: Number(detalleTemp.idDeposito) || 1,
                cantidad: Number(detalleTemp.cantidad),
                precio: Number(detalleTemp.precio),
                gravada: Number(detalleTemp.gravada),
                monExenta: calc.exenta,
                monGravada: calc.gravada,
                monIva: calc.iva,
                monTotal: calc.total
            };
            const detalles = [...f.detalles, nuevoDetalle];
            const totales = detalles.reduce((acc, d) => ({
                exenta: acc.exenta + (Number(d.monExenta) || 0),
                gravada: acc.gravada + (Number(d.monGravada) || 0),
                iva: acc.iva + (Number(d.monIva) || 0),
                total: acc.total + (Number(d.monTotal) || 0)
            }), { exenta: 0, gravada: 0, iva: 0, total: 0 });
            return {
                ...f,
                detalles,
                monExenta: totales.exenta,
                monGravada: totales.gravada,
                monIva: totales.iva,
                monTotal: totales.total
            };
        });
        setDetalleTemp({ idProducto: '', descripcion: '', cantidad: 1, precio: 0, gravada: 10, idDeposito: 1 });
        setBusquedaProducto('');
    };

    // Actualiza cantidad o precio de una linea del detalle y recalcula IVA + totales
    const actualizarDetalle = (idProducto, campo, valor) => {
        setFormulario(f => {
            const detalles = f.detalles.map(d => {
                if (d.idProducto !== idProducto) return d;
                const cantidad = campo === 'cantidad' ? Number(valor) || 0 : Number(d.cantidad);
                const precio   = campo === 'precio'   ? Number(valor) || 0 : Number(d.precio);
                const calc = calcularLinea(cantidad, precio, d.gravada);
                return {
                    ...d,
                    cantidad, precio,
                    monExenta: calc.exenta,
                    monGravada: calc.gravada,
                    monIva: calc.iva,
                    monTotal: calc.total
                };
            });
            const totales = detalles.reduce((acc, d) => ({
                exenta: acc.exenta + (Number(d.monExenta) || 0),
                gravada: acc.gravada + (Number(d.monGravada) || 0),
                iva: acc.iva + (Number(d.monIva) || 0),
                total: acc.total + (Number(d.monTotal) || 0)
            }), { exenta: 0, gravada: 0, iva: 0, total: 0 });
            return {
                ...f, detalles,
                monExenta: totales.exenta,
                monGravada: totales.gravada,
                monIva: totales.iva,
                monTotal: totales.total
            };
        });
    };

    const quitarDetalle = (idProducto) => {
        setFormulario(f => {
            const detalles = f.detalles.filter(d => d.idProducto !== idProducto);
            const totales = detalles.reduce((acc, d) => ({
                exenta: acc.exenta + (Number(d.monExenta) || 0),
                gravada: acc.gravada + (Number(d.monGravada) || 0),
                iva: acc.iva + (Number(d.monIva) || 0),
                total: acc.total + (Number(d.monTotal) || 0)
            }), { exenta: 0, gravada: 0, iva: 0, total: 0 });
            return {
                ...f, detalles,
                monExenta: totales.exenta,
                monGravada: totales.gravada,
                monIva: totales.iva,
                monTotal: totales.total
            };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formulario.idProveedor)          { alert('Seleccione el proveedor'); return; }
        if (!formulario.tipoDoc)              { alert('Seleccione el Tipo de Documento'); return; }
        if (!formulario.nfactura || Number(formulario.nfactura) <= 0)
                                              { alert('Ingrese el N° de factura'); return; }
        if (!formulario.timbrado || String(formulario.timbrado).trim() === '')
                                              { alert('Ingrese el Timbrado'); return; }
        if (!formulario.sucursal || Number(formulario.sucursal) <= 0)
                                              { alert('Ingrese la Sucursal'); return; }
        if (!formulario.boca || String(formulario.boca).trim() === '')
                                              { alert('Ingrese la Boca / Punto de Expedicion'); return; }
        if (!formulario.fecha)                { alert('Ingrese la Fecha'); return; }
        if (!formulario.idFormapago)          { alert('Seleccione la Forma de Pago'); return; }
        if (!formulario.vence)                { alert('Ingrese el Vencimiento'); return; }
        if (!formulario.idMoneda)             { alert('Seleccione la Moneda'); return; }
        if (formulario.cambio === '' || formulario.cambio == null || Number(formulario.cambio) <= 0)
                                              { alert('Ingrese el Tipo de Cambio (mayor a 0)'); return; }
        if (formulario.detalles.length === 0) { alert('Agregue al menos un producto al detalle'); return; }

        const payload = {
            idProveedor: Number(formulario.idProveedor),
            nfactura: Number(formulario.nfactura),
            tipoDoc: formulario.tipoDoc,
            timbrado: formulario.timbrado,
            fecha: formulario.fecha,
            vence: formulario.vence || null,
            idFormapago: formulario.idFormapago,
            idMoneda: formulario.idMoneda || 'PYG',
            cambio: Number(formulario.cambio) || 1,
            monExenta: Number(formulario.monExenta) || 0,
            monGravada: Number(formulario.monGravada) || 0,
            monIva: Number(formulario.monIva) || 0,
            monTotal: Number(formulario.monTotal) || 0,
            obs: formulario.obs || '',
            sucursal: Number(formulario.sucursal) || 1,
            boca: formulario.boca || '',
            nrofacex: formulario.nrofacex || ' ',
            fservicio: Number(formulario.fservicio) || 0,
            precioiva: Number(formulario.precioiva) || 0,
            detalles: formulario.detalles.map(d => ({
                idProducto: d.idProducto,
                nomProducto: d.nomProducto,
                idDeposito: d.idDeposito,
                cantidad: d.cantidad,
                precio: d.precio,
                monExenta: d.monExenta,
                monGravada: d.monGravada,
                monIva: d.monIva
            })),
            // Opcion B: recepciones que esta factura consume. El backend las
            // vincula ANTES de insertar detalles, para que el trigger de
            // sfacomprad NO mueva stock (la recepcion ya lo hizo).
            recepcionesVinculadas: recepcionesVinculadas.map(r => ({
                idSucursal: r.id_sucursal,
                boca:       r.boca,
                nrecep:     r.nrecep
            }))
        };

        try {
            await axios.post('/api/facompra', payload);

            // Registrar consumo de las ordenes usadas (bridge sordencompra_factura).
            // El backend suma cantidad_facturada por linea y cierra automaticamente
            // cuando cantidad_facturada >= cantidad.
            const lineasPorOrden = {};
            for (const d of formulario.detalles) {
                if (!d.ordencompraOrigen) continue;
                if (!lineasPorOrden[d.ordencompraOrigen]) lineasPorOrden[d.ordencompraOrigen] = [];
                lineasPorOrden[d.ordencompraOrigen].push({
                    idProducto: d.idProducto,
                    cantidad: Number(d.cantidad),
                    precio: Number(d.precio)
                });
            }
            let ordenesRegistradas = 0;
            for (const [ordenId, lineas] of Object.entries(lineasPorOrden)) {
                try {
                    await axios.post(`/api/ordencompra/${ordenId}/registrar-facturacion`, {
                        ffIdProveedor: payload.idProveedor,
                        ffNfactura:    payload.nfactura,
                        ffTipoDoc:     payload.tipoDoc,
                        ffTimbrado:    payload.timbrado,
                        lineas
                    });
                    ordenesRegistradas++;
                } catch (e) {
                    console.warn(`No se pudo registrar facturacion en orden ${ordenId}:`, e);
                }
            }

            const partes = ['Factura de compra registrada'];
            if (ordenesRegistradas > 0)
                partes.push(`${ordenesRegistradas} orden(es) actualizada(s)`);
            if (recepcionesVinculadas.length > 0)
                partes.push(`${recepcionesVinculadas.length} recepcion(es) vinculada(s) (stock NO duplicado)`);
            alert(partes.join('. ') + '.');
            cerrarModal();
            cargar();
        } catch (error) {
            const msg = error.response?.data?.message || 'Error al guardar';
            alert(msg);
        }
    };

    // ===== Ver =====
    const verCompra = async (c) => {
        try {
            const r = await axios.get(`/api/facompra/${c.idProveedor}/${c.nfactura}/${c.tipoDoc}/${encodeURIComponent(c.timbrado)}`);
            setCompraVer(r.data);
        } catch (e) {
            console.error(e);
            setCompraVer(c);
        }
        setModalVerAbierto(true);
    };

    // ===== Anular =====
    const anular = async (c) => {
        if (c.estado === 9) { alert('Ya esta anulada'); return; }
        if (!window.confirm(`Anular la factura #${c.nfactura} del proveedor ${c.nomProveedor}?`)) return;
        try {
            await axios.put('/api/facompra/anular', null, {
                params: {
                    idProveedor: c.idProveedor, nfactura: c.nfactura,
                    tipoDoc: c.tipoDoc, timbrado: c.timbrado
                }
            });
            alert('Factura anulada');
            cargar();
        } catch (error) {
            const msg = error.response?.data?.message || 'Error al anular';
            alert(msg);
        }
    };

    // ===== Excel =====
    const exportarExcel = () => {
        if (filtradas.length === 0) { alert('No hay datos para exportar'); return; }
        const datos = filtradas.map(c => ({
            'Proveedor ID': c.idProveedor,
            'Proveedor': c.nomProveedor || '',
            'N° Factura': c.nfactura,
            'Tipo Doc': c.tipoDoc,
            'Timbrado': c.timbrado,
            'Sucursal': c.sucursal,
            'Boca': c.boca,
            'Fecha': formatearFecha(c.fecha),
            'Vence': formatearFecha(c.vence),
            'Moneda': c.idMoneda,
            'Cambio': Number(c.cambio || 1),
            'Mon. Exenta': Number(c.monExenta || 0),
            'Mon. Gravada': Number(c.monGravada || 0),
            'Mon. IVA': Number(c.monIva || 0),
            'Mon. Total': Number(c.monTotal || 0),
            'Saldo': Number(c.saldo || 0),
            'Estado': c.estado === 9 ? 'Anulada' : c.estado === 0 ? 'Pendiente' : c.estado,
            'Observaciones': c.obs
        }));
        const ws = XLSX.utils.json_to_sheet(datos);
        ws['!cols'] = Object.keys(datos[0]).map(k => ({ wch: Math.max(k.length, 14) }));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Compras');
        XLSX.writeFile(wb, `compras_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    return (
        <div className="envase-container">
            <div className="header-seccion">
                <h2>Facturas de Compra</h2>
                <div className="acciones-header" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', border: '1px solid #ddd', borderRadius: '5px', padding: '4px 8px', backgroundColor: '#fff' }}>
                        <label style={{ fontSize: '12px', color: '#555' }}>📅 Desde</label>
                        <input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)}
                            style={{ padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }} />
                        <label style={{ fontSize: '12px', color: '#555' }}>Hasta</label>
                        <input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)}
                            style={{ padding: '4px 6px', border: '1px solid #ccc', borderRadius: '4px', fontSize: '13px' }} />
                        {(fechaDesde || fechaHasta) && (
                            <button type="button" onClick={() => { setFechaDesde(''); setFechaHasta(''); }}
                                style={{ padding: '2px 6px', border: 'none', backgroundColor: 'transparent', color: '#d97706', cursor: 'pointer' }}>✕</button>
                        )}
                    </div>
                    <input type="text" placeholder="Buscar..." value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="input-busqueda" />
                    <button onClick={abrirModalNuevo} className="btn btn-nuevo">➕ Nueva Compra</button>
                    <button onClick={exportarExcel} className="btn btn-exportar">📊 Exportar Excel</button>
                </div>
            </div>

            <div className="tabla-container">
                <table className="tabla-datos">
                    <thead>
                        <tr>
                            <th>Proveedor</th>
                            <th>N° Factura</th>
                            <th>Tipo</th>
                            <th>Timbrado</th>
                            <th>Fecha</th>
                            <th>Vence</th>
                            <th>Moneda</th>
                            <th style={{ textAlign: 'right' }}>Total</th>
                            <th style={{ textAlign: 'right' }}>Saldo</th>
                            <th>Estado</th>
                            <th>Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {paginadas.length === 0 ? (
                            <tr><td colSpan="11" className="sin-datos">No hay facturas de compra</td></tr>
                        ) : paginadas.map(c => (
                            <tr key={`${c.idProveedor}-${c.nfactura}-${c.tipoDoc}-${c.timbrado}`}
                                style={c.estado === 9 ? { opacity: 0.5 } : {}}>
                                <td>{c.idProveedor} - {c.nomProveedor || '-'}</td>
                                <td>{c.nfactura}</td>
                                <td>{c.tipoDoc}</td>
                                <td>{c.timbrado}</td>
                                <td>{formatearFecha(c.fecha)}</td>
                                <td>{formatearFecha(c.vence)}</td>
                                <td>
                                    <span style={{
                                        display: 'inline-block',
                                        padding: '2px 8px',
                                        borderRadius: '10px',
                                        backgroundColor: c.idMoneda === 'PYG' ? '#E4F2FC' : '#fff7ed',
                                        color: c.idMoneda === 'PYG' ? '#004F9F' : '#d97706',
                                        fontSize: '11px',
                                        fontWeight: '600'
                                    }}>
                                        {c.idMoneda || 'PYG'}
                                        {c.idMoneda && c.idMoneda !== 'PYG' && c.cambio && Number(c.cambio) !== 1 && (
                                            <span style={{ marginLeft: '4px', fontWeight: 'normal' }}>
                                                @ {formatearNumero(c.cambio, 2)}
                                            </span>
                                        )}
                                    </span>
                                </td>
                                <td style={{ textAlign: 'right' }}>{formatearNumero(c.monTotal)}</td>
                                <td style={{ textAlign: 'right' }}>{formatearNumero(c.saldo)}</td>
                                <td style={c.estado === 9 ? { color: '#c62828', fontWeight: 'bold' } : {}}>
                                    {c.estado === 9 ? 'Anulada' : c.estado === 0 ? 'Pendiente' : c.estado}
                                </td>
                                <td>
                                    <button onClick={() => verCompra(c)} className="btn-accion" title="Ver detalle">👁</button>
                                    <button onClick={() => anular(c)} className="btn-accion" title="Anular"
                                        disabled={c.estado === 9}
                                        style={c.estado === 9 ? { opacity: 0.3 } : { color: '#c62828' }}>✕</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="paginacion-container">
                <div className="paginacion-info">
                    <span>Mostrando {filtradas.length > 0 ? inicio + 1 : 0} a {Math.min(inicio + porPagina, filtradas.length)} de {filtradas.length}</span>
                    <select value={porPagina} onChange={(e) => { setPorPagina(Number(e.target.value)); setPagina(1); }} className="select-registros">
                        <option value={10}>10</option><option value={20}>20</option><option value={50}>50</option>
                    </select>
                </div>
                <div className="paginacion-controles">
                    <button onClick={() => setPagina(1)} disabled={pagina === 1} className="btn-paginacion">|&lt;</button>
                    <button onClick={() => setPagina(p => p - 1)} disabled={pagina === 1} className="btn-paginacion">&lt;</button>
                    <span className="paginacion-numero">{pagina} / {totalPaginas}</span>
                    <button onClick={() => setPagina(p => p + 1)} disabled={pagina >= totalPaginas} className="btn-paginacion">&gt;</button>
                    <button onClick={() => setPagina(totalPaginas)} disabled={pagina >= totalPaginas} className="btn-paginacion">&gt;|</button>
                </div>
            </div>

            {/* Modal Nueva Compra */}
            {modalAbierto && (
                <div className="atm-modal-overlay" onClick={cerrarModal}>
                    <div className="atm-modal atm-modal-xl" onClick={(e) => e.stopPropagation()}>
                        <div className="atm-modal-header">
                            <h3>Nueva Factura de Compra</h3>
                            <button onClick={cerrarModal} className="atm-modal-close">×</button>
                        </div>

                        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
                            <div className="atm-modal-body">

                                {/* Cabecera */}
                                <div style={{ marginBottom: '15px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#fafafa' }}>
                                    <h4 style={{ margin: '0 0 10px 0', color: '#004F9F', borderBottom: '2px solid #004F9F', paddingBottom: '5px' }}>Cabecera</h4>

                                    {/* Proveedor */}
                                    <div className="form-group" style={{ position: 'relative', marginBottom: '10px' }}>
                                        <label>Proveedor *</label>
                                        {formulario.idProveedor ? (
                                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', padding: '8px', backgroundColor: '#E4F2FC', borderRadius: '5px', flexWrap: 'wrap' }}>
                                                <strong>{formulario.idProveedor}</strong> - {formulario.nomProveedor}
                                                <span style={{ fontSize: '12px', color: '#555' }}>({formulario.rucProveedor})</span>
                                                <button type="button"
                                                    onClick={() => setModalOrdenAbierto(true)}
                                                    disabled={ordenesActivas.length === 0}
                                                    title={ordenesActivas.length === 0
                                                        ? 'El proveedor no tiene ordenes de compra activas'
                                                        : `${ordenesActivas.length} orden(es) activa(s) disponible(s)`}
                                                    style={{
                                                        marginLeft: 'auto',
                                                        padding: '6px 12px',
                                                        border: '1px solid ' + (ordenesActivas.length > 0 ? '#004F9F' : '#ccc'),
                                                        backgroundColor: ordenesActivas.length > 0 ? '#004F9F' : '#f0f0f0',
                                                        color: ordenesActivas.length > 0 ? '#fff' : '#999',
                                                        borderRadius: '5px',
                                                        cursor: ordenesActivas.length > 0 ? 'pointer' : 'not-allowed',
                                                        fontWeight: '600',
                                                        fontSize: '12px'
                                                    }}>
                                                    📋 Obtener Orden de Compra
                                                    {ordenesActivas.length > 0 && ` (${ordenesActivas.length})`}
                                                </button>
                                                <button type="button"
                                                    onClick={() => setModalRecepAbierto(true)}
                                                    disabled={recepcionesActivas.length === 0}
                                                    title={recepcionesActivas.length === 0
                                                        ? 'El proveedor no tiene recepciones activas'
                                                        : `${recepcionesActivas.length} recepcion(es) activa(s) - stock ya cargado`}
                                                    style={{
                                                        padding: '6px 12px',
                                                        border: '1px solid ' + (recepcionesActivas.length > 0 ? '#2E7D32' : '#ccc'),
                                                        backgroundColor: recepcionesActivas.length > 0 ? '#2E7D32' : '#f0f0f0',
                                                        color: recepcionesActivas.length > 0 ? '#fff' : '#999',
                                                        borderRadius: '5px',
                                                        cursor: recepcionesActivas.length > 0 ? 'pointer' : 'not-allowed',
                                                        fontWeight: '600',
                                                        fontSize: '12px'
                                                    }}>
                                                    📦 Obtener Recepcion
                                                    {recepcionesActivas.length > 0 && ` (${recepcionesActivas.length})`}
                                                    {recepcionesVinculadas.length > 0 && ` · ✓${recepcionesVinculadas.length}`}
                                                </button>
                                                <button type="button"
                                                    onClick={() => {
                                                        setFormulario(f => ({ ...f, idProveedor: '', nomProveedor: '', rucProveedor: '' }));
                                                        setOrdenesActivas([]);
                                                        setRecepcionesActivas([]);
                                                        setRecepcionesVinculadas([]);
                                                    }}
                                                    style={{ background: 'none', border: 'none', color: '#d97706', cursor: 'pointer' }}>✕ cambiar</button>
                                            </div>
                                        ) : (
                                            <input type="text" placeholder="Buscar proveedor por nombre o RUC (min 2 chars)..."
                                                value={busquedaProv} onChange={(e) => buscarProvAPI(e.target.value)}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                        )}
                                        {provsEncontrados.length > 0 && !formulario.idProveedor && (
                                            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff',
                                                border: '1px solid #ccc', borderRadius: '5px', maxHeight: '250px', overflowY: 'auto',
                                                zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                                                {provsEncontrados.map(p => (
                                                    <div key={p.id_proveedor} onClick={() => seleccionarProveedor(p)}
                                                        style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '13px' }}
                                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#E4F2FC'}
                                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}>
                                                        <strong>{p.id_proveedor}</strong> - {p.nom_proveedor}
                                                        {p.ruc_prov && <span style={{ color: '#555', marginLeft: '8px' }}>({p.ruc_prov})</span>}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr', gap: '10px' }}>
                                        <div className="form-group">
                                            <label>Tipo Doc *</label>
                                            <select value={formulario.tipoDoc}
                                                onChange={(e) => setFormulario(f => ({ ...f, tipoDoc: e.target.value }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                                {TIPOS_DOC.map(t => <option key={t.codigo} value={t.codigo}>{t.codigo} - {t.nombre}</option>)}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>N° Factura *</label>
                                            <input type="number" value={formulario.nfactura}
                                                onChange={(e) => setFormulario(f => ({ ...f, nfactura: e.target.value }))} required
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>Timbrado *</label>
                                            <input type="text" value={formulario.timbrado} maxLength={25}
                                                onChange={(e) => setFormulario(f => ({ ...f, timbrado: e.target.value }))} required
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>Sucursal *</label>
                                            <input type="number" value={formulario.sucursal} required min="1"
                                                onChange={(e) => setFormulario(f => ({ ...f, sucursal: e.target.value }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>Boca / Punto Exp. *</label>
                                            <input type="text" value={formulario.boca} maxLength={4} required
                                                placeholder="Ej: 001"
                                                onChange={(e) => setFormulario(f => ({ ...f, boca: e.target.value }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>Fecha *</label>
                                            <input type="date" value={formulario.fecha} required
                                                onChange={(e) => cambiarFecha(e.target.value)}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>Forma de Pago *</label>
                                            <select value={formulario.idFormapago}
                                                onChange={(e) => seleccionarFormaPago(e.target.value)}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                                <option value="">-- Seleccione --</option>
                                                {formasPago.map(fp => (
                                                    <option key={fp.idFpago} value={fp.idFpago}>
                                                        {fp.idFpago}{fp.vdias1 != null ? ` (${fp.vdias1} dias)` : ''}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Vencimiento *</label>
                                            <input type="date" value={formulario.vence} required
                                                onChange={(e) => setFormulario(f => ({ ...f, vence: e.target.value }))}
                                                title="Calculado automaticamente segun la forma de pago; se puede editar"
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>Moneda *</label>
                                            <select value={formulario.idMoneda} required
                                                onChange={(e) => setFormulario(f => ({ ...f, idMoneda: e.target.value }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                                <option value="">-- Seleccione --</option>
                                                <option value="PYG">PYG (Guaranies)</option>
                                                <option value="USD">USD (Dolares)</option>
                                                <option value="EUR">EUR (Euros)</option>
                                                <option value="BRL">BRL (Reales)</option>
                                                <option value="ARS">ARS (Pesos AR)</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>Cambio *</label>
                                            <input
                                                type="number"
                                                inputMode="decimal"
                                                min="0.0001"
                                                step="any"
                                                required
                                                value={formulario.cambio}
                                                onChange={(e) => setFormulario(f => ({ ...f, cambio: e.target.value }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px', textAlign: 'right' }}
                                            />
                                        </div>
                                        {formulario.tipoDoc === 'FEX' && (
                                            <div className="form-group" style={{ gridColumn: 'span 2' }}>
                                                <label>Nro Factura Extranjera</label>
                                                <input type="text" value={formulario.nrofacex} maxLength={25}
                                                    onChange={(e) => setFormulario(f => ({ ...f, nrofacex: e.target.value }))}
                                                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                            </div>
                                        )}
                                        <div className="form-group" style={{ gridColumn: 'span 4' }}>
                                            <label>Observaciones</label>
                                            <input type="text" value={formulario.obs} maxLength={160}
                                                onChange={(e) => setFormulario(f => ({ ...f, obs: e.target.value }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }} />
                                        </div>
                                    </div>
                                </div>

                                {/* Agregar producto */}
                                <div style={{ marginBottom: '15px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#EDF1FB' }}>
                                    <h4 style={{ margin: '0 0 10px 0', color: '#004F9F' }}>Agregar producto al detalle</h4>
                                    <div style={{ display: 'grid', gridTemplateColumns: '3fr 1fr 1fr 1fr auto', gap: '10px', alignItems: 'end' }}>
                                        <div className="form-group" style={{ position: 'relative' }}>
                                            <label>Producto (min 2 caracteres)</label>
                                            <div style={{ display: 'flex', gap: '4px' }}>
                                                <input type="text" placeholder="Buscar por descripcion o codigo..."
                                                    value={busquedaProducto}
                                                    onChange={(e) => buscarProductosAPI(e.target.value)}
                                                    readOnly={!!detalleTemp.idProducto}
                                                    style={{
                                                        flex: 1, padding: '8px', border: '1px solid #ddd', borderRadius: '5px',
                                                        backgroundColor: detalleTemp.idProducto ? '#f0f0f0' : '#fff',
                                                        cursor: detalleTemp.idProducto ? 'not-allowed' : 'text'
                                                    }} />
                                                {detalleTemp.idProducto && (
                                                    <button type="button"
                                                        title="Limpiar seleccion"
                                                        onClick={() => {
                                                            setDetalleTemp({ idProducto: '', descripcion: '', cantidad: 1, precio: 0, gravada: 10, idDeposito: 1 });
                                                            setBusquedaProducto('');
                                                        }}
                                                        style={{
                                                            padding: '4px 10px', border: '1px solid #d97706',
                                                            backgroundColor: '#fff7ed', color: '#d97706',
                                                            borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold'
                                                        }}>✕</button>
                                                )}
                                            </div>
                                            {productosEncontrados.length > 0 && (
                                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#fff',
                                                    border: '1px solid #ccc', borderRadius: '5px', maxHeight: '250px', overflowY: 'auto',
                                                    zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
                                                    {productosEncontrados.map(p => (
                                                        <div key={p.id_producto} onClick={() => seleccionarProducto(p)}
                                                            style={{ padding: '8px', cursor: 'pointer', borderBottom: '1px solid #eee', fontSize: '13px' }}
                                                            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f9ff'}
                                                            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#fff'}>
                                                            <strong>{p.id_producto}</strong> - {p.descripcion}
                                                            <span style={{ marginLeft: '8px', color: '#555', fontSize: '12px' }}>
                                                                IVA {p.gravada}%
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                        <div className="form-group">
                                            <label>Cantidad *</label>
                                            <input type="number" value={detalleTemp.cantidad} min="0" step="any"
                                                onChange={(e) => setDetalleTemp(d => ({ ...d, cantidad: Number(e.target.value) }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px', textAlign: 'right' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>Precio Unit.</label>
                                            <input type="number" value={detalleTemp.precio} min="0" step="any"
                                                onChange={(e) => setDetalleTemp(d => ({ ...d, precio: Number(e.target.value) }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px', textAlign: 'right' }} />
                                        </div>
                                        <div className="form-group">
                                            <label>IVA %</label>
                                            <select value={detalleTemp.gravada}
                                                onChange={(e) => setDetalleTemp(d => ({ ...d, gravada: Number(e.target.value) }))}
                                                style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '5px' }}>
                                                <option value={0}>0% Exenta</option>
                                                <option value={5}>5%</option>
                                                <option value={10}>10%</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <button type="button" onClick={agregarDetalle}
                                                disabled={!detalleTemp.idProducto}
                                                className="btn btn-nuevo" style={{ marginTop: '5px' }}>+ Agregar</button>
                                        </div>
                                    </div>
                                </div>

                                {/* Detalle */}
                                {formulario.detalles.length > 0 && (
                                    <div style={{ marginBottom: '15px', padding: '12px', border: '1px solid #e0e0e0', borderRadius: '6px', backgroundColor: '#fafafa' }}>
                                        <h4 style={{ margin: '0 0 10px 0', color: '#004F9F' }}>Detalle ({formulario.detalles.length} productos)</h4>
                                        <table className="tabla-datos" style={{ fontSize: '13px' }}>
                                            <thead>
                                                <tr>
                                                    <th>ID</th><th>Producto</th>
                                                    <th style={{ textAlign: 'right' }}>Cant.</th>
                                                    <th style={{ textAlign: 'right' }}>Precio</th>
                                                    <th>IVA</th>
                                                    <th style={{ textAlign: 'right' }}>Exenta</th>
                                                    <th style={{ textAlign: 'right' }}>Gravada</th>
                                                    <th style={{ textAlign: 'right' }}>IVA</th>
                                                    <th style={{ textAlign: 'right' }}>Total</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formulario.detalles.map(d => {
                                                    const tips = [];
                                                    if (d.ordencompraOrigen) tips.push(`OC #${d.ordencompraOrigen}`);
                                                    if (d.recepOrigen && d.recepOrigen.length > 0)
                                                        tips.push('Recep ' + d.recepOrigen.map(r => `#${r}`).join(', '));
                                                    return (
                                                    <tr key={d.idProducto} title={tips.join(' · ')}>
                                                        <td>
                                                            {d.idProducto}
                                                            {d.ordencompraOrigen && (
                                                                <span style={{
                                                                    display: 'inline-block',
                                                                    marginLeft: '4px',
                                                                    padding: '1px 6px',
                                                                    borderRadius: '10px',
                                                                    backgroundColor: '#E4F2FC',
                                                                    color: '#004F9F',
                                                                    fontSize: '10px',
                                                                    fontWeight: '600'
                                                                }}>OC #{d.ordencompraOrigen}</span>
                                                            )}
                                                            {d.recepOrigen && d.recepOrigen.length > 0 && (
                                                                <span style={{
                                                                    display: 'inline-block',
                                                                    marginLeft: '4px',
                                                                    padding: '1px 6px',
                                                                    borderRadius: '10px',
                                                                    backgroundColor: '#E8F5E9',
                                                                    color: '#2E7D32',
                                                                    fontSize: '10px',
                                                                    fontWeight: '600'
                                                                }}>📦 REC {d.recepOrigen.length > 1 ? `x${d.recepOrigen.length}` : `#${d.recepOrigen[0]}`}</span>
                                                            )}
                                                        </td>
                                                        <td>{d.nomProducto}</td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            <input type="number" min="0" step="any"
                                                                value={d.cantidad}
                                                                onChange={(e) => actualizarDetalle(d.idProducto, 'cantidad', e.target.value)}
                                                                style={{ width: '90px', padding: '3px 6px', border: '1px solid #ddd', borderRadius: '4px', textAlign: 'right', fontSize: '13px' }} />
                                                        </td>
                                                        <td style={{ textAlign: 'right' }}>
                                                            <input type="number" min="0" step="any"
                                                                value={d.precio}
                                                                onChange={(e) => actualizarDetalle(d.idProducto, 'precio', e.target.value)}
                                                                style={{ width: '110px', padding: '3px 6px', border: '1px solid #ddd', borderRadius: '4px', textAlign: 'right', fontSize: '13px' }} />
                                                        </td>
                                                        <td>{d.gravada}%</td>
                                                        <td style={{ textAlign: 'right' }}>{formatearNumero(d.monExenta, 2)}</td>
                                                        <td style={{ textAlign: 'right' }}>{formatearNumero(d.monGravada, 2)}</td>
                                                        <td style={{ textAlign: 'right' }}>{formatearNumero(d.monIva, 2)}</td>
                                                        <td style={{ textAlign: 'right', fontWeight: '600' }}>{formatearNumero(d.monTotal, 2)}</td>
                                                        <td><button type="button" onClick={() => quitarDetalle(d.idProducto)} className="btn-accion">🗑</button></td>
                                                    </tr>
                                                    );
                                                })}
                                                <tr style={{ backgroundColor: '#E4F2FC', fontWeight: 'bold' }}>
                                                    <td colSpan="5" style={{ textAlign: 'right' }}>TOTALES</td>
                                                    <td style={{ textAlign: 'right' }}>{formatearNumero(formulario.monExenta)}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatearNumero(formulario.monGravada)}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatearNumero(formulario.monIva)}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatearNumero(formulario.monTotal)}</td>
                                                    <td></td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <div className="atm-modal-footer">
                                <button type="button" onClick={cerrarModal} className="atm-btn-cancelar">Cancelar</button>
                                <button type="submit" className="atm-btn-primary">Registrar Compra</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Modal Seleccionar Orden de Compra */}
            {modalOrdenAbierto && (
                <div className="atm-modal-overlay" onClick={() => setModalOrdenAbierto(false)}>
                    <div className="atm-modal atm-modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="atm-modal-header">
                            <h3>Ordenes de Compra activas de {formulario.nomProveedor}</h3>
                            <button onClick={() => setModalOrdenAbierto(false)} className="atm-modal-close">×</button>
                        </div>
                        <div className="atm-modal-body">
                            {ordenesActivas.length === 0 ? (
                                <p style={{ padding: '20px', textAlign: 'center', color: '#555' }}>
                                    No hay ordenes activas para este proveedor.
                                </p>
                            ) : (
                                <>
                                    <p style={{ marginBottom: '10px', color: '#555', fontSize: '13px' }}>
                                        Seleccione una orden para volcar sus lineas al detalle de la factura.
                                        La orden se marcara como cerrada al guardar la factura.
                                    </p>
                                    <table className="tabla-datos" style={{ fontSize: '13px' }}>
                                        <thead>
                                            <tr>
                                                <th>N° Orden</th>
                                                <th>Fecha</th>
                                                <th>Para Fecha</th>
                                                <th>Forma Pago</th>
                                                <th>Entrega</th>
                                                <th style={{ textAlign: 'right' }}>Total</th>
                                                <th>Lineas</th>
                                                <th>Observacion</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {ordenesActivas.map(o => (
                                                <tr key={o.ordencompra}>
                                                    <td>{o.ordencompra}</td>
                                                    <td>{formatearFecha(o.fecha)}</td>
                                                    <td>{formatearFecha(o.parafecha)}</td>
                                                    <td>{o.id_fpago || '-'}</td>
                                                    <td>{o.tipoentrega || '-'}</td>
                                                    <td style={{ textAlign: 'right' }}>{formatearNumero(o.mon_total)}</td>
                                                    <td style={{ textAlign: 'center' }}>{o.lineas_abiertas}</td>
                                                    <td style={{ fontSize: '12px', color: '#555' }}>{o.observacion || ''}</td>
                                                    <td>
                                                        <button type="button"
                                                            onClick={() => seleccionarOrden(o)}
                                                            className="atm-btn-primary"
                                                            style={{ padding: '5px 10px', fontSize: '12px' }}>
                                                            Volcar
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </div>
                        <div className="atm-modal-footer">
                            <button type="button" onClick={() => setModalOrdenAbierto(false)} className="atm-btn-cancelar">
                                Cerrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Recepciones de Materiales (Opcion B) */}
            {modalRecepAbierto && (
                <div className="atm-modal-overlay" onClick={() => setModalRecepAbierto(false)}>
                    <div className="atm-modal atm-modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="atm-modal-header">
                            <h3>Recepciones de Materiales activas · {formulario.nomProveedor}</h3>
                            <button onClick={() => setModalRecepAbierto(false)} className="atm-modal-close">×</button>
                        </div>
                        <div className="atm-modal-body">
                            {recepcionesActivas.length === 0 ? (
                                <p style={{ padding: '20px', textAlign: 'center', color: '#555' }}>
                                    No hay recepciones activas para este proveedor.
                                </p>
                            ) : (
                                <>
                                    <div style={{ padding: '10px', backgroundColor: '#E8F5E9',
                                                  borderRadius: '5px', marginBottom: '10px', fontSize: '13px',
                                                  color: '#1B5E20' }}>
                                        <strong>📦 Recepciones ya cargadas al stock.</strong> Marque las que esta factura cubre.
                                        Al guardar, el stock <strong>NO</strong> se sumara nuevamente (ya se hizo al recepcionar),
                                        pero se actualizara el costo real y quedaran vinculadas.
                                    </div>
                                    <table className="tabla-datos" style={{ fontSize: '13px' }}>
                                        <thead>
                                            <tr>
                                                <th style={{ width: '40px', textAlign: 'center' }}>Vinc.</th>
                                                <th>N° Recep.</th>
                                                <th>Fecha</th>
                                                <th>Deposito</th>
                                                <th>Remito Prov.</th>
                                                <th>F. Remito</th>
                                                <th>OC</th>
                                                <th style={{ textAlign: 'center' }}>Lineas</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {recepcionesActivas.map(r => {
                                                const key = `${r.id_sucursal}-${r.boca}-${r.nrecep}`;
                                                const vinc = recepcionesVinculadas
                                                    .find(x => x.id_sucursal === r.id_sucursal
                                                            && x.boca === r.boca
                                                            && x.nrecep === r.nrecep);
                                                return (
                                                    <tr key={key}
                                                        style={{ backgroundColor: vinc ? '#E8F5E9' : 'transparent' }}>
                                                        <td style={{ textAlign: 'center' }}>
                                                            <input type="checkbox" checked={!!vinc}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setRecepcionesVinculadas(prev => [...prev, r]);
                                                                    } else {
                                                                        setRecepcionesVinculadas(prev =>
                                                                            prev.filter(x => !(x.id_sucursal === r.id_sucursal
                                                                                    && x.boca === r.boca
                                                                                    && x.nrecep === r.nrecep)));
                                                                    }
                                                                }} />
                                                        </td>
                                                        <td><strong>#{r.nrecep}</strong>
                                                            <span style={{ color: '#888', fontSize: '11px' }}> · {r.id_sucursal}-{r.boca}</span>
                                                        </td>
                                                        <td>{formatearFecha(r.fecha)}</td>
                                                        <td>{r.nom_deposito || r.id_deposito}</td>
                                                        <td>{r.remito_prov || '-'}</td>
                                                        <td>{r.fecha_remito ? formatearFecha(r.fecha_remito) : '-'}</td>
                                                        <td>{r.ordencompra ? `OC #${r.ordencompra}` : '-'}</td>
                                                        <td style={{ textAlign: 'center' }}>{r.lineas}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </>
                            )}
                        </div>
                        {/* Preview de lineas que se van a volcar */}
                        {aggRecep.length > 0 && (
                            <div style={{ padding: '10px 20px', borderTop: '1px solid #ddd', background: '#FAFAFA' }}>
                                <div style={{ fontWeight: '600', color: '#2E7D32', marginBottom: '6px' }}>
                                    ✓ Al confirmar se volcaran estas lineas al detalle de la factura:
                                </div>
                                <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ background: '#E8F5E9' }}>
                                            <th style={{ padding: '4px', textAlign: 'left' }}>Cod</th>
                                            <th style={{ padding: '4px', textAlign: 'left' }}>Descripcion</th>
                                            <th style={{ padding: '4px', textAlign: 'center' }}>IVA</th>
                                            <th style={{ padding: '4px', textAlign: 'right' }}>Cant. recibida</th>
                                            <th style={{ padding: '4px', textAlign: 'right' }}>Precio ref.</th>
                                            <th style={{ padding: '4px', textAlign: 'left' }}>Origen</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {aggRecep.map(a => (
                                            <tr key={a.id_producto} style={{ borderBottom: '1px solid #eee' }}>
                                                <td style={{ padding: '4px' }}>{a.id_producto}</td>
                                                <td style={{ padding: '4px' }}>{a.descripcion}</td>
                                                <td style={{ padding: '4px', textAlign: 'center' }}>{a.gravada}%</td>
                                                <td style={{ padding: '4px', textAlign: 'right' }}>
                                                    <strong>{formatearNumero(a.cantidad_recibida, 2)}</strong>
                                                </td>
                                                <td style={{ padding: '4px', textAlign: 'right' }}>{formatearNumero(a.costoulcom, 2)}</td>
                                                <td style={{ padding: '4px', color: '#666', fontSize: '11px' }}>
                                                    {(a.origen || []).map(o => `#${o}`).join(', ')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                <div style={{ marginTop: '6px', fontSize: '11px', color: '#666' }}>
                                    Podras ajustar cantidad y precio (por si la factura difiere) directamente en la tabla del detalle antes de guardar.
                                </div>
                            </div>
                        )}
                        <div className="atm-modal-footer">
                            <span style={{ marginRight: 'auto', color: '#2E7D32', fontWeight: '600' }}>
                                {recepcionesVinculadas.length} recepcion(es) · {aggRecep.length} linea(s) a volcar
                            </span>
                            <button type="button" onClick={() => setModalRecepAbierto(false)} className="atm-btn-cancelar">
                                Cancelar
                            </button>
                            <button type="button" onClick={volcarRecepcionesADetalle}
                                disabled={aggRecep.length === 0}
                                className="atm-btn-primary"
                                style={{ opacity: aggRecep.length === 0 ? 0.5 : 1 }}>
                                Confirmar y volcar al detalle
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal Ver */}
            {modalVerAbierto && compraVer && (
                <div className="atm-modal-overlay" onClick={() => setModalVerAbierto(false)}>
                    <div className="atm-modal atm-modal-lg" onClick={(e) => e.stopPropagation()}>
                        <div className="atm-modal-header">
                            <h3>Factura de Compra #{compraVer.nfactura}</h3>
                            <button onClick={() => setModalVerAbierto(false)} className="atm-modal-close">×</button>
                        </div>
                        <div className="atm-modal-body">
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '15px' }}>
                                <div><strong>Proveedor:</strong> {compraVer.idProveedor} - {compraVer.nomProveedor}</div>
                                <div><strong>Tipo Doc:</strong> {compraVer.tipoDoc}</div>
                                <div><strong>Timbrado:</strong> {compraVer.timbrado}</div>
                                <div><strong>Sucursal / Boca:</strong> {compraVer.sucursal || '-'} / {compraVer.boca || '-'}</div>
                                <div><strong>Fecha:</strong> {formatearFecha(compraVer.fecha)}</div>
                                <div><strong>Vence:</strong> {formatearFecha(compraVer.vence)}</div>
                                <div><strong>Forma Pago:</strong> {compraVer.idFormapago || '-'}</div>
                                <div><strong>Moneda:</strong> {compraVer.idMoneda} (cambio {compraVer.cambio})</div>
                                <div><strong>Exenta:</strong> {formatearNumero(compraVer.monExenta)}</div>
                                <div><strong>Gravada:</strong> {formatearNumero(compraVer.monGravada)}</div>
                                <div><strong>IVA:</strong> {formatearNumero(compraVer.monIva)}</div>
                                <div><strong>Total:</strong> {formatearNumero(compraVer.monTotal)}</div>
                                <div><strong>Saldo:</strong> {formatearNumero(compraVer.saldo)}</div>
                                <div><strong>Estado:</strong> {compraVer.estado === 9 ? 'Anulada' : 'Pendiente'}</div>
                                <div style={{ gridColumn: 'span 2' }}><strong>Obs:</strong> {compraVer.obs}</div>
                            </div>

                            <h4 style={{ color: '#004F9F' }}>Detalle</h4>
                            <table className="tabla-datos" style={{ fontSize: '13px' }}>
                                <thead>
                                    <tr>
                                        <th>ID</th><th>Producto</th><th>Deposito</th>
                                        <th style={{ textAlign: 'right' }}>Cant.</th>
                                        <th style={{ textAlign: 'right' }}>Precio</th>
                                        <th style={{ textAlign: 'right' }}>Exenta</th>
                                        <th style={{ textAlign: 'right' }}>Gravada</th>
                                        <th style={{ textAlign: 'right' }}>IVA</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(compraVer.detalles || []).map(d => (
                                        <tr key={d.idProducto}>
                                            <td>{d.idProducto}</td>
                                            <td>{d.nomProducto}</td>
                                            <td>{d.idDeposito}</td>
                                            <td style={{ textAlign: 'right' }}>{formatearNumero(d.cantidad, 2)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatearNumero(d.precio, 2)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatearNumero(d.monExenta, 2)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatearNumero(d.monGravada, 2)}</td>
                                            <td style={{ textAlign: 'right' }}>{formatearNumero(d.monIva, 2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default SFacompra;
